import { Button } from '@ipfabric/ui-components/Button/Button';
import { FormCheckbox } from '@ipfabric/ui-components/FormCheckbox/FormCheckbox';
import { Link } from '@ipfabric/ui-components/Link/Link';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import { OverlayModal } from 'components/OverlayModal/OverlayModal';
import type { ImmutableVerifyJob } from 'containers/App/Header/JobManagement/jobTypes';
import type { Control } from 'react-hook-form';
import type { TechsupportFormValues } from './Techsupport.utils';
import { useLazySnapshotDeviceLabels } from './useLazySnapshotDeviceLabels';

export type GenerateTechsupportModalData = {
  values: TechsupportFormValues;
  limitSn: string[] | null;
  allSnapshotDevicesSelected: boolean;
  selectedFeatureLabels: string[];
};

type GenerateTechsupportSideModalProps = {
  control: Control<TechsupportFormValues>;
  data: GenerateTechsupportModalData | null;
  formSubmitting: boolean;
  onClose: () => void;
  onGenerate: () => void;
  onLoadSelectedSnapshotDeviceLabels: (limitSn: string[]) => Promise<string[]>;
  onVerifySnapshot: (values: TechsupportFormValues, limitSn: string[]) => void;
  running: boolean;
  show: boolean;
  snapshotId: string | null;
  verifyRunningJob: ImmutableVerifyJob | undefined;
};

const SNAPSHOT_DEVICE_DETAIL_LABELS = new Set([
  'Current snapshot data',
  'CLI logs from snapshots',
  'DB dump',
]);

const formatDeviceCountLabel = (count: number): string =>
  `${count} ${count === 1 ? 'device' : 'devices'}`;

const formatIncluded = (snapshotDetails: string[]): string => {
  if (snapshotDetails.length === 0) {
    return '';
  }

  if (snapshotDetails.length === 1) {
    return ` (including ${snapshotDetails[0]})`;
  }

  return ` (including ${snapshotDetails.slice(0, -1).join(', ')} and ${snapshotDetails[snapshotDetails.length - 1]})`;
};

const renameModalFeatureLabel = (label: string): string =>
  label === 'System environmental data' ? 'General environmental data' : label;

const buildSnapshotDetails = (selectedFeatureLabels: string[]): string[] => {
  const details: string[] = [];
  if (selectedFeatureLabels.includes('CLI logs from snapshots')) {
    details.push('CLI logs');
  }
  if (selectedFeatureLabels.includes('DB dump')) {
    details.push('DB dump');
  }
  return details;
};

const buildDeviceSummary = (data: GenerateTechsupportModalData): string => {
  const snapshotDetails = buildSnapshotDetails(data.selectedFeatureLabels);
  const deviceCountText =
    data.limitSn === null || data.allSnapshotDevicesSelected
      ? 'All devices'
      : formatDeviceCountLabel(data.limitSn.length);
  return `${deviceCountText}${formatIncluded(snapshotDetails)}`;
};

type DeviceSummaryItemProps = {
  data: GenerateTechsupportModalData;
  onLoadLabels: (limitSn: string[]) => Promise<string[]>;
};

const DeviceSummaryItem = ({ data, onLoadLabels }: DeviceSummaryItemProps) => {
  const canExpand =
    data.limitSn !== null &&
    data.limitSn.length > 0 &&
    !data.allSnapshotDevicesSelected;
  const { expanded, labels, loading, toggle } = useLazySnapshotDeviceLabels({
    limitSn: data.limitSn,
    onLoad: onLoadLabels,
  });

  return (
    <li>
      {buildDeviceSummary(data)}
      {canExpand ? (
        <div>
          <Link
            icon={expanded ? 'fa-angle-up' : 'fa-angle-down'}
            iconPosition='end'
            loading={loading}
            onClick={() => {
              void toggle();
            }}
            renderAs='button'
            size='small'
            type='button'
          >
            {expanded ? 'Hide devices' : 'Show devices'}
          </Link>
          {expanded ? (
            <ul className='m-0 mt-1 flex list-none flex-col gap-0 pl-0 text-sm leading-none'>
              {(labels ?? []).map((label, index) => (
                <li key={data.limitSn?.[index] ?? `${index}-${label}`}>
                  {label}
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
    </li>
  );
};

type ModalFooterProps = {
  control: Control<TechsupportFormValues>;
  data: GenerateTechsupportModalData;
  formSubmitting: boolean;
  onClose: () => void;
  onGenerate: () => void;
  onVerifySnapshot: (values: TechsupportFormValues, limitSn: string[]) => void;
  running: boolean;
  snapshotId: string | null;
  verifyRunningJob: ImmutableVerifyJob | undefined;
};

const ModalFooter = ({
  control,
  data,
  formSubmitting,
  onClose,
  onGenerate,
  onVerifySnapshot,
  running,
  snapshotId,
  verifyRunningJob,
}: ModalFooterProps) => {
  const verifyVisible = data.limitSn !== null && data.limitSn.length > 0;
  const verifyDisabled = Boolean(verifyRunningJob) || snapshotId === null;
  const requiresActiveSnapshot = data.values.snapshot;
  const generateDisabled =
    running ||
    (requiresActiveSnapshot && snapshotId === null) ||
    formSubmitting;

  return (
    <div className='border-0 border-t border-solid border-gray-300 pt-4'>
      <div className='flex flex-col gap'>
        {verifyVisible ? (
          <div className='flex items-center justify-between gap'>
            <div className='flex items-center gap-xs'>
              <FormCheckbox
                control={control}
                name='verifySnapshot'
                description='Create a snapshot using data included in the file'
                disabled={verifyDisabled}
              />
              <TooltipIcon
                size='sm'
                content='Creates a new snapshot from the selected devices. Scope is fixed: service logs and CLI handling follow your selections above; other options (databases, system logs, environmental data) do not apply.'
              />
            </div>
            {process.env.NODE_ENV === 'development' ? (
              <Button
                variant='plain'
                icon='fa-play'
                size='small'
                type='button'
                disabled={verifyDisabled || formSubmitting}
                elementProps={{ 'aria-label': 'Verify snapshot now' }}
                onClick={() => {
                  if (data.limitSn === null) {
                    return;
                  }

                  onVerifySnapshot(data.values, data.limitSn);
                }}
              />
            ) : null}
          </div>
        ) : null}
        <div className='flex flex-row gap-xs'>
          <Button
            variant='primary'
            disabled={generateDisabled}
            loading={formSubmitting}
            onClick={onGenerate}
            className='flex-1'
          >
            Generate file
          </Button>
          <Button variant='outline' onClick={onClose} className='flex-1'>
            Cancel
          </Button>
        </div>
      </div>
    </div>
  );
};

export const GenerateTechsupportSideModal = ({
  control,
  data,
  formSubmitting,
  onClose,
  onGenerate,
  onLoadSelectedSnapshotDeviceLabels,
  onVerifySnapshot,
  running,
  show,
  snapshotId,
  verifyRunningJob,
}: GenerateTechsupportSideModalProps) => {
  if (!show || data === null) {
    return null;
  }

  const visibleFeatureLabels = data.selectedFeatureLabels
    .filter((label) => !SNAPSHOT_DEVICE_DETAIL_LABELS.has(label))
    .map(renameModalFeatureLabel);

  return (
    <OverlayModal
      onClose={onClose}
      size='small'
      contentClassName='flex flex-col p-4 gap min-h-[446px] max-h-[calc(100vh-4rem)] overflow-hidden'
    >
      <div className='text-default text-level-3 font-semibold'>
        Confirm Generation
      </div>
      <div className='cross-browser-scrollbar flex-auto min-h-0 overflow-auto'>
        <div className='flex flex-col gap'>
          <div className='text-default text-level-5 font-semibold'>
            File will contain:
          </div>
          <ul className='m-0 flex list-disc text-base flex-col pl-7'>
            {visibleFeatureLabels.map((label) => (
              <li key={label} className='leading'>
                {label}
              </li>
            ))}
            {data.values.snapshot ? (
              <DeviceSummaryItem
                key={data.limitSn?.join('|') ?? '__all__'}
                data={data}
                onLoadLabels={onLoadSelectedSnapshotDeviceLabels}
              />
            ) : null}
          </ul>
        </div>
      </div>
      <ModalFooter
        control={control}
        data={data}
        formSubmitting={formSubmitting}
        onClose={onClose}
        onGenerate={onGenerate}
        onVerifySnapshot={onVerifySnapshot}
        running={running}
        snapshotId={snapshotId}
        verifyRunningJob={verifyRunningJob}
      />
    </OverlayModal>
  );
};
